import { Component, forwardRef, Input, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { AbstractControl, FormControl, ControlValueAccessor, NG_VALUE_ACCESSOR, NG_VALIDATORS, Validator, Validators } from '@angular/forms';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { dateFPipe } from '../pipes/index';

import { PageContentService, SessionStorageService } from '../../core/index.service';
import { PageContent } from '../../core/index.model';

@Component({
    selector: 'pnx-date',
    templateUrl: './date.control.html',
    providers: [
        dateFPipe,
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DateControl), multi: true },
        { provide: NG_VALIDATORS, useExisting: forwardRef(() => DateControl), multi: true }
    ]
})
export class DateControl implements ControlValueAccessor, Validator {

    @Input() labelId: string = '';
    @Input() defaultLabel: string = '';
    @Input() placeholderId: string = '';
    @Input() defaultPlaceholder: string = '';
    @Input() pagecontent: PageContent;

    @Input() controlid: string = 'dateControl1';
    @Input() mandatory: boolean = false;
    @Input() readonly: boolean = false;
    @Input() containerstyle: string = 'row';
    @Input() labelstyle: string = 'control-label';
    @Input() datecontainerstyle: string = 'col-md-9';
    @Input() datectrlstyle: string = '';
    @Input() label: string = '';
    @Input() placeholder: string = '';
    @Input('value') _value = '';
    @Input() control: FormControl;
    @Input() disable: boolean = false;
    @Output() onDateSelect: EventEmitter<any> = new EventEmitter<any>();
    @Output() onBlurdate: EventEmitter<any> = new EventEmitter<any>();
    @Output() onKeyUp: EventEmitter<any> = new EventEmitter<any>();

    disabled: boolean = false;
    dateVal: string = '';
    isGreaterYear: boolean = false;

    constructor(private dFPipe: dateFPipe,
        private pageContentService: PageContentService,
        private storageService: SessionStorageService) {
        
    }

    ngOnInit() {
        if (this.pagecontent) {
            if (!this.pageContentService.pageContent) {
                this.pageContentService.pageContent = this.pagecontent;
            }
        }
    }

    ngOnChanges(Changes: SimpleChanges) {         
        this.setDisabledState(this.disable);
        this.propagateChange(this.value);
        if (this.control) { this.validate(this.control); }

        let labelValue = (this.labelId) ? this.pageContentService.getKeyTxt(this.labelId, this.storageService.getLang()) : this.label;
        let placeHolderValue = (this.placeholderId) ? this.pageContentService.getKeyTxt(this.placeholderId, this.storageService.getLang()) : this.placeholder;

        this.label = (labelValue) ? labelValue : this.defaultLabel;
        this.placeholder = (placeHolderValue) ? placeHolderValue : this.defaultPlaceholder;
    }

    date = new FormControl();
    
    propagateChange: any = () => { };

    get value() {
        return this._value;
    }

    set value(val) {
        this._value = val;
        if (val != null && (this.defaultLabel == "Date of Birth" || this.defaultLabel == "DOB")) {
            var date = new Date(val);
            var year = date.getFullYear();
            if (year < 1915) {     //165062-In CSP invalid DOB accepted while creating a CORS entry
                this.isGreaterYear = true;
                this.propagateChange(val);

            }
        }
        else {

            this.propagateChange(val);
        }
    }

    onDateChange(value: string) {    
        const newValue: string = value;
        this.value = value;

        if (this.defaultLabel == "Date of Birth" || this.defaultLabel == "DOB") {
            var date = new Date(newValue);
            const year = date.getFullYear();
            if (year >= 1915) {  //165062-In CSP invalid DOB accepted while creating a CORS entry
                if (this.dateVal && this.dateVal.length == 10) {
                    this.onDateSelect.emit(this.value);
                    this.isGreaterYear = false;
                }
                else if (this.dateVal && this.dateVal.length < 10) {
                    this.date.setValue(null);
                    this.dateVal = null;
                    this.onKeyUp.emit(this.value);
                    this.isGreaterYear = false;
                }
                else if (this.value) {
                    this.onDateSelect.emit(this.value);
                    this.isGreaterYear = false;
                }
            }
            else {
                this.onDateSelect.emit(this.value);
                this.isGreaterYear = true;
            }
        }
        else {
                if (this.dateVal && this.dateVal.length == 10) {
                    this.onDateSelect.emit(this.value);
                    this.isGreaterYear = false;
                }
                else if (this.dateVal && this.dateVal.length < 10) {
                    this.date.setValue(null);
                    this.dateVal = null;
                    this.onKeyUp.emit(this.value);
                    this.isGreaterYear = false;
                }
                else if (this.value) {
                    this.onDateSelect.emit(this.value);
                    this.isGreaterYear = false;
                }
            }        
    }

    onBlurEvent(value: string) {
        this.value = value;

        this.onBlurdate.emit(this.value);
    }

    onDateInput(value: string) {    
        const newValue: string = value;
        let date = this.dFPipe.transform(newValue);
        this.value = date;

        if (!this.value) {
            this.date.setValue(this.value);
        }
        else if (this.value && this.value.length == 10) {
            let newDate = new Date(this.value);
            this.date.setValue(newDate);
        }
        
        this.onKeyUp.emit(value); // Added by Punithavathy for CRM# 82175 on 02/08/2018 to emit keyup event while entering values in the date control

        this.dateVal = date;
    }

    writeValue(value): void {
        this.value = value;
        this.date.setValue(this.value);
    }

    registerOnChange(fn): void {
        this.propagateChange = fn;
    }

    registerOnTouched(fn): void { }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;

        if (this.disabled) {
            this.date.disable();
        }
        else {
            this.date.enable();
        }
    }

    public validate(parentControl: AbstractControl): { [key: string]: any } {

        const val = parentControl.value;

        if (this.mandatory) {

            this.date.setValidators(Validators.required);
            parentControl.setValidators(Validators.required);

            if (!parentControl.value) {
                parentControl.setValue(null);
            }

            if (!val) {
                return { valid: false };
            }
        }
        else {

            this.date.clearValidators();
            parentControl.clearValidators();

            if (!parentControl.value) {
                parentControl.setValue(null);
            }
        }

        return null;
    }

}


// WEBPACK FOOTER //
// D:/NewPhoenixScrum/PhoenixScrum/Main/CitizenSevicesEx/CSClient/CSClient/client/app/shared/controls/date.control.ts